import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import dayjs from 'dayjs';
import { App, Breadcrumb, Button, DatePicker, Dropdown, Input, Popconfirm, Segmented, Select, Skeleton, Tooltip, Typography, Upload } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExpandOutlined,
  FileOutlined,
  LinkOutlined,
  MoreOutlined,
  PaperClipOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { TaskActivityItem, TaskCommentItem, TaskDetail, UpdateTaskInput } from '@crm/shared';
import {
  useAddComment,
  useCreateTask,
  useDeleteAttachment,
  useDeleteComment,
  useDeleteTask,
  useDuplicateTask,
  useProjectSections,
  useTaskDetail,
  useUpdateComment,
  useUpdateTask,
  useUploadAttachment,
} from '../../features/tasks/api';
import { dueFromDay } from '../../features/tasks/view';
import { useProjectScope } from '../../features/projects/scope';
import { useFeatures } from '../../features/settings/api';
import { useAuth } from '../../auth/AuthProvider';
import { errorMessage, fileUrl } from '../../api/client';
import { ProjectSelect } from '../../components/ProjectSelect';
import { RichTextEditor } from '../../components/RichTextEditor';
import { LoadError } from '../../components/states';
import { fmtDate, fmtDateTime } from '../../utils/format';
import { palette } from '../../app/theme';
import { ActivityText, AssigneeCell, AssigneeSelect, CompleteToggle, DueText, priorityOptions, useTaskUsers, useToggleComplete, UserAvatar } from './TaskBits';

export const taskLink = (id: string) => `${window.location.origin}/tasks/${id}`;

const clean = (html: string) => DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });

/** Whether a stored description is HTML from the editor or plain text from an older task. */
const asHtml = (text: string | null) => (!text ? '' : /<\/?[a-z][\s\S]*>/i.test(text) ? text : `<p>${text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br>')}</p>`);

function fileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <div className="task-field-label">{label}</div>
      <div className="task-field-value">{children}</div>
    </>
  );
}

/** Title that saves when you leave it or press Enter. */
function TitleEditor({ task }: { task: TaskDetail }) {
  const { message } = App.useApp();
  const update = useUpdateTask();
  const [title, setTitle] = useState(task.title);
  useEffect(() => setTitle(task.title), [task.id, task.title]);
  const save = () => {
    const t = title.trim();
    if (!t) return setTitle(task.title);
    if (t !== task.title) update.mutate({ id: task.id, title: t }, { onError: (e) => message.error(errorMessage(e)) });
  };
  // a plain textarea that grows with the title (antd's autosizing one rendered twice inside the drawer)
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [title]);
  return (
    <textarea
      ref={ref}
      rows={1}
      className="task-detail-title"
      value={title}
      aria-label="Task name"
      maxLength={300}
      onChange={(e) => setTitle(e.target.value.replace(/\n/g, ''))}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
        if (e.key === 'Escape') setTitle(task.title);
      }}
      style={{ textDecoration: task.completedAt ? 'line-through' : undefined }}
    />
  );
}

/** Rich-text description, saved a second after typing stops (and when the panel closes). */
function DescriptionEditor({ task }: { task: TaskDetail }) {
  const update = useUpdateTask();
  const [value, setValue] = useState(() => asHtml(task.description));
  const saved = useRef(asHtml(task.description));
  const latest = useRef(value);
  latest.current = value;
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const flush = useRef<() => void>(() => undefined);
  flush.current = () => {
    if (latest.current === saved.current) return;
    saved.current = latest.current;
    setState('saving');
    update.mutate({ id: task.id, description: latest.current || null }, { onSuccess: () => setState('saved'), onError: () => setState('idle') });
  };
  useEffect(() => {
    if (value === saved.current) return;
    const t = window.setTimeout(() => flush.current(), 1000);
    return () => window.clearTimeout(t);
  }, [value]);
  useEffect(() => () => flush.current(), []);
  return (
    <div>
      <RichTextEditor value={value} onChange={setValue} mergeTags={false} mentions minHeight={90} placeholder="Add more detail to this task…" />
      <Typography.Text type="secondary" style={{ fontSize: 12, visibility: state === 'idle' ? 'hidden' : undefined }}>
        {state === 'saving' ? 'Saving…' : 'Saved'}
      </Typography.Text>
    </div>
  );
}

function Subtasks({ task, onOpen }: { task: TaskDetail; onOpen: (id: string) => void }) {
  const { message } = App.useApp();
  const create = useCreateTask();
  const [title, setTitle] = useState('');
  const add = async () => {
    if (!title.trim()) return;
    try {
      await create.mutateAsync({ title: title.trim(), parentId: task.id, assigneeUserId: null });
      setTitle('');
    } catch (e) {
      message.error(errorMessage(e));
    }
  };
  const done = task.subtasks.filter((s) => s.completedAt).length;
  return (
    <div>
      <div className="task-detail-heading">
        Subtasks
        {task.subtasks.length > 0 && (
          <Typography.Text type="secondary" style={{ fontWeight: 400, marginLeft: 8 }}>
            {done} of {task.subtasks.length} done
          </Typography.Text>
        )}
      </div>
      <div className="task-subtasks">
        {task.subtasks.map((s) => (
          <div key={s.id} className={`task-subtask${s.completedAt ? ' task-row-done' : ''}`} onClick={() => onOpen(s.id)}>
            <CompleteToggle task={s} size={16} />
            <span className="task-title-text" style={{ flex: 1 }}>
              {s.title}
            </span>
            {(s.subtaskCount ?? 0) > 0 && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{s.subtasksDone}/{s.subtaskCount}</Typography.Text>}
            <DueText task={s} />
            {s.assignee && <AssigneeCell user={s.assignee} showName={false} />}
          </div>
        ))}
        <Input
          variant="borderless"
          prefix={<PlusOutlined style={{ color: palette.textTertiary }} />}
          placeholder="Add a subtask, then Enter"
          aria-label="New subtask"
          value={title}
          disabled={create.isPending}
          onChange={(e) => setTitle(e.target.value)}
          onPressEnter={() => void add()}
        />
      </div>
    </div>
  );
}

function Attachments({ task }: { task: TaskDetail }) {
  const { message, modal } = App.useApp();
  const upload = useUploadAttachment(task.id);
  const remove = useDeleteAttachment();
  return (
    <div>
      <div className="task-detail-heading">Attachments</div>
      {task.attachments.length > 0 && (
        <div className="task-attachments">
          {task.attachments.map((a) => {
            const image = /^image\/(png|jpe?g|gif|webp|avif)$/.test(a.contentType);
            return (
              <div key={a.id} className="task-attachment">
                <a href={fileUrl(a.fileId)} target="_blank" rel="noreferrer" className="task-attachment-thumb" aria-label={`Open ${a.fileName}`}>
                  {image ? <img src={fileUrl(a.fileId)} alt="" /> : <FileOutlined />}
                </a>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <Typography.Text ellipsis style={{ display: 'block' }} title={a.fileName}>
                    {a.fileName}
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {fileSize(a.size)} · {a.uploadedBy?.name ?? 'Someone'} · {fmtDate(a.createdAt)}
                  </Typography.Text>
                </div>
                <Tooltip title="Download">
                  <Button type="text" size="small" icon={<DownloadOutlined />} href={`${fileUrl(a.fileId)}?download=${encodeURIComponent(a.fileName)}`} aria-label={`Download ${a.fileName}`} />
                </Tooltip>
                <Tooltip title="Remove">
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    aria-label={`Remove ${a.fileName}`}
                    onClick={() =>
                      modal.confirm({
                        title: `Remove ${a.fileName}?`,
                        okText: 'Remove',
                        okButtonProps: { danger: true },
                        onOk: () => remove.mutateAsync(a.id).catch((e) => message.error(errorMessage(e))),
                      })
                    }
                  />
                </Tooltip>
              </div>
            );
          })}
        </div>
      )}
      <Upload.Dragger
        multiple
        showUploadList={false}
        customRequest={({ file, onSuccess, onError }) =>
          upload.mutateAsync(file as File).then(
            (r) => onSuccess?.(r),
            (e) => {
              message.error(`${(file as File).name}: ${errorMessage(e)}`);
              onError?.(e as Error);
            },
          )
        }
        style={{ padding: 0 }}
      >
        <div style={{ padding: '6px 0', color: palette.textSecondary }}>
          <PaperClipOutlined /> {upload.isPending ? 'Uploading…' : 'Drop files here or click to attach (up to 20 MB)'}
        </div>
      </Upload.Dragger>
    </div>
  );
}

function Comment({ comment, mine }: { comment: TaskCommentItem; mine: boolean }) {
  const { message } = App.useApp();
  const update = useUpdateComment();
  const remove = useDeleteComment();
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(comment.body);
  const html = useMemo(() => clean(comment.body), [comment.body]);
  return (
    <div className="task-feed-item">
      {comment.author ? <UserAvatar user={comment.author} size={28} /> : <span style={{ width: 28 }} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <Typography.Text strong>{comment.author?.name ?? 'Someone'}</Typography.Text>
          <Tooltip title={fmtDateTime(comment.createdAt)}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {dayjs(comment.createdAt).fromNow()}
              {comment.updatedAt !== comment.createdAt && dayjs(comment.updatedAt).diff(comment.createdAt, 'second') > 2 ? ' · edited' : ''}
            </Typography.Text>
          </Tooltip>
          <span style={{ flex: 1 }} />
          {mine && !editing && (
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  { key: 'edit', label: 'Edit' },
                  { key: 'delete', label: 'Delete', danger: true },
                ],
                onClick: ({ key }) => {
                  if (key === 'edit') {
                    setBody(comment.body);
                    setEditing(true);
                  } else remove.mutate(comment.id, { onError: (e) => message.error(errorMessage(e)) });
                },
              }}
            >
              <Button type="text" size="small" icon={<MoreOutlined />} aria-label="Comment actions" />
            </Dropdown>
          )}
        </div>
        {editing ? (
          <div style={{ marginTop: 6 }}>
            <RichTextEditor value={body} onChange={setBody} mergeTags={false} mentions minHeight={60} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <Button
                type="primary"
                size="small"
                loading={update.isPending}
                disabled={!body}
                onClick={() => update.mutate({ id: comment.id, body }, { onSuccess: () => setEditing(false), onError: (e) => message.error(errorMessage(e)) })}
              >
                Save
              </Button>
              <Button size="small" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="task-rich" dangerouslySetInnerHTML={{ __html: html }} />
        )}
      </div>
    </div>
  );
}

function HistoryLine({ item }: { item: TaskActivityItem }) {
  return (
    <div className="task-feed-item task-feed-history">
      <span className="task-feed-dot" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <ActivityText item={item} />{' '}
        <Tooltip title={fmtDateTime(item.createdAt)}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            · {dayjs(item.createdAt).fromNow()}
          </Typography.Text>
        </Tooltip>
      </div>
    </div>
  );
}

function Feed({ task }: { task: TaskDetail }) {
  const { message } = App.useApp();
  const { user } = useAuth();
  const add = useAddComment(task.id);
  const [mode, setMode] = useState<'comments' | 'all'>('all');
  const [body, setBody] = useState('');
  const items = useMemo(() => {
    const comments = task.comments.map((c) => ({ at: c.createdAt, node: <Comment key={`c${c.id}`} comment={c} mine={c.author?.id === user?.id} /> }));
    const history = mode === 'all' ? task.history.filter((h) => h.type !== 'comment').map((h) => ({ at: h.createdAt, node: <HistoryLine key={`h${h.id}`} item={h} /> })) : [];
    return [...comments, ...history].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  }, [task.comments, task.history, mode, user?.id]);
  const submit = async () => {
    if (!body) return;
    try {
      await add.mutateAsync(body);
      setBody('');
    } catch (e) {
      message.error(errorMessage(e));
    }
  };
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div className="task-detail-heading" style={{ margin: 0 }}>
          Activity
        </div>
        <Segmented
          size="small"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'all', label: 'All activity' },
            { value: 'comments', label: `Comments${task.comments.length ? ` (${task.comments.length})` : ''}` },
          ]}
        />
      </div>
      <div className="task-feed">
        {items.length ? items.map((i) => i.node) : <Typography.Text type="secondary">No comments yet.</Typography.Text>}
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        {user && <UserAvatar user={user} size={28} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <RichTextEditor value={body} onChange={setBody} mergeTags={false} mentions minHeight={60} placeholder="Write a comment…" />
          <Button type="primary" size="small" style={{ marginTop: 8 }} disabled={!body} loading={add.isPending} onClick={() => void submit()}>
            Comment
          </Button>
        </div>
      </div>
    </div>
  );
}

interface Props {
  taskId: string;
  /** open another task here (a subtask or the parent) */
  onOpen: (id: string) => void;
  onClose?: () => void;
  /** shown when the panel is not already the full page */
  fullPageLink?: boolean;
}

/** Everything about one task: fields, description, subtasks, files, comments and history. */
export function TaskDetailPanel({ taskId, onOpen, onClose, fullPageLink }: Props) {
  const { message, modal } = App.useApp();
  const detail = useTaskDetail(taskId);
  const { names } = useTaskUsers();
  const update = useUpdateTask(names);
  const toggle = useToggleComplete();
  const duplicate = useDuplicateTask();
  const remove = useDeleteTask();
  const scope = useProjectScope();
  const features = useFeatures();
  const task = detail.data;
  const sections = useProjectSections(task?.projectId, !!task && !task.parentId);

  if (detail.isError) return <LoadError error={detail.error} onRetry={detail.refetch} title="Could not load this task" />;
  if (!task) return <Skeleton active paragraph={{ rows: 10 }} />;

  const set = (patch: UpdateTaskInput) =>
    update.mutate({ id: task.id, ...patch }, { onError: (e) => message.error(errorMessage(e)) });
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(taskLink(task.id));
      message.success('Task link copied');
    } catch {
      message.info(taskLink(task.id));
    }
  };
  const done = !!task.completedAt;
  const linked = [
    task.contact && { to: `/contacts/${task.contact.id}`, label: [task.contact.firstName, task.contact.lastName].filter(Boolean).join(' ') || 'Contact' },
    task.organization && { to: `/organizations/${task.organization.id}`, label: task.organization.name },
    task.deal && features.enabled('pipelines') && { to: `/deals/${task.deal.id}`, label: task.deal.title },
  ].filter(Boolean) as { to: string; label: string }[];

  return (
    <div className="task-detail">
      <div className="task-detail-bar">
        <Button
          type={done ? 'primary' : 'default'}
          icon={<CheckOutlined />}
          className={done ? 'task-done-button' : undefined}
          onClick={(e) => toggle(task, e)}
        >
          {done ? 'Completed' : 'Mark complete'}
        </Button>
        <span style={{ flex: 1 }} />
        <Tooltip title="Copy task link">
          <Button type="text" icon={<LinkOutlined />} onClick={() => void copyLink()}>
            #{task.number}
          </Button>
        </Tooltip>
        {fullPageLink && (
          <Tooltip title="Open as a page">
            <Link to={`/tasks/${task.id}`}>
              <Button type="text" icon={<ExpandOutlined />} aria-label="Open as a page" />
            </Link>
          </Tooltip>
        )}
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              { key: 'copy', icon: <LinkOutlined />, label: 'Copy task link' },
              { key: 'duplicate', icon: <CopyOutlined />, label: 'Duplicate task' },
              { type: 'divider' },
              { key: 'delete', icon: <DeleteOutlined />, label: 'Delete task', danger: true },
            ],
            onClick: ({ key }) => {
              if (key === 'copy') void copyLink();
              if (key === 'duplicate')
                duplicate.mutate(task.id, {
                  onSuccess: (copy) => {
                    message.success('Task duplicated');
                    onOpen(copy.id);
                  },
                  onError: (e) => message.error(errorMessage(e)),
                });
              if (key === 'delete')
                modal.confirm({
                  title: `Delete "${task.title}"?`,
                  content: task.subtaskCount ? 'Its subtasks, comments and files are deleted too.' : 'Its comments and files are deleted too.',
                  okText: 'Delete',
                  okButtonProps: { danger: true },
                  onOk: () =>
                    remove.mutateAsync(task.id).then(
                      () => {
                        message.success('Task deleted');
                        if (task.parent) onOpen(task.parent.id);
                        else onClose?.();
                      },
                      (e) => message.error(errorMessage(e)),
                    ),
                });
            },
          }}
        >
          <Button type="text" icon={<MoreOutlined />} aria-label="More task actions" />
        </Dropdown>
        {onClose && <Button type="text" icon={<CloseOutlined />} aria-label="Close task" onClick={onClose} />}
      </div>

      <div className="task-detail-body">
        {task.ancestors.length > 0 && (
          <Breadcrumb
            style={{ marginBottom: 4 }}
            items={task.ancestors.map((a) => ({
              title: (
                <a
                  href={`/tasks/${a.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    onOpen(a.id);
                  }}
                >
                  {a.title}
                </a>
              ),
            }))}
          />
        )}
        <TitleEditor key={`title-${task.id}`} task={task} />

        <div className="task-fields">
          <Field label="Assignee">
            <AssigneeSelect value={task.assignee?.id ?? null} onChange={(v) => set({ assigneeUserId: v })} variant="borderless" style={{ minWidth: 220 }} />
          </Field>
          <Field label="Due date">
            <DatePicker
              variant="borderless"
              value={task.dueAt ? dayjs(task.dueAt) : null}
              onChange={(d) => set({ dueAt: dueFromDay(d) })}
              placeholder="No due date"
              aria-label="Due date"
              presets={[
                { label: 'Today', value: dayjs() },
                { label: 'Tomorrow', value: dayjs().add(1, 'day') },
                { label: 'Next week', value: dayjs().add(1, 'week').startOf('week').add(1, 'day') },
              ]}
            />
          </Field>
          <Field label="Priority">
            <Select allowClear variant="borderless" placeholder="No priority" value={task.priority ?? undefined} onChange={(v) => set({ priority: v ?? null })} options={priorityOptions} style={{ minWidth: 160 }} aria-label="Priority" />
          </Field>
          {scope.enabled && !task.parentId && (
            <Field label="Project">
              <ProjectSelect value={task.projectId} onChange={(v) => set({ projectId: v })} style={{ minWidth: 220 }} />
            </Field>
          )}
          {!task.parentId && (
            <Field label="Section">
              <Select
                allowClear
                variant="borderless"
                placeholder="No section"
                value={task.sectionId ?? undefined}
                onChange={(v) => set({ sectionId: v ?? null })}
                options={(sections.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
                style={{ minWidth: 200 }}
                aria-label="Section"
                notFoundContent="This project has no sections yet"
              />
            </Field>
          )}
          {linked.length > 0 && (
            <Field label="Linked to">
              <span style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: '4px 11px' }}>
                {linked.map((l) => (
                  <Link key={l.to} to={l.to}>
                    {l.label}
                  </Link>
                ))}
              </span>
            </Field>
          )}
          <Field label="Created">
            <Typography.Text type="secondary" style={{ padding: '4px 11px', display: 'inline-block' }}>
              {fmtDate(task.createdAt)}
              {task.createdBy && ` by ${task.createdBy.name}`}
            </Typography.Text>
          </Field>
        </div>

        <div className="task-detail-heading">Description</div>
        <DescriptionEditor key={`description-${task.id}`} task={task} />

        <Subtasks task={task} onOpen={onOpen} />
        <Attachments task={task} />
        <Feed task={task} />
      </div>
    </div>
  );
}
